/* STL quote tool.

   Parses an uploaded STL or 3MF entirely in the browser, computes true
   mesh volume by signed-tetrahedron summation, renders a preview, and
   prices the job from rates configured in the theme editor.

   No data leaves the browser until the customer submits.  */

(function () {
  'use strict';

  var root = document.querySelector('[data-quote-tool]');
  if (!root) return;

  var cfg = JSON.parse(root.getAttribute('data-config'));

  // Wired up by sections/quote-tool.liquid when a customer is logged in.
  var quotesEndpoint = root.getAttribute('data-quotes-endpoint') || '/apps/ninja/quotes';
  var loggedIn = !!root.getAttribute('data-customer');

  var els = {
    drop: root.querySelector('[data-drop]'),
    dropZone: root.querySelector('[data-drop-zone]'),
    file: root.querySelector('[data-file]'),
    fileName: root.querySelector('[data-file-name]'),
    canvas: root.querySelector('[data-canvas]'),
    material: root.querySelector('[data-material]'),
    color: root.querySelector('[data-color]'),
    infill: root.querySelector('[data-infill]'),
    infillOut: root.querySelector('[data-infill-out]'),
    quality: root.querySelector('[data-quality]'),
    qty: root.querySelector('[data-qty]'),
    results: root.querySelector('[data-results]'),
    dims: root.querySelector('[data-dims]'),
    vol: root.querySelector('[data-vol]'),
    grams: root.querySelector('[data-grams]'),
    time: root.querySelector('[data-time]'),
    price: root.querySelector('[data-price]'),
    unit: root.querySelector('[data-unit-price]'),
    printer: root.querySelector('[data-printer]'),
    surcharge: root.querySelector('[data-surcharge]'),
    stlUrl: root.querySelector('[data-stl-url]'),
    previewUrl: root.querySelector('[data-preview-url]'),
    fileIds: root.querySelector('[data-file-ids]'),
    uploadStatus: root.querySelector('[data-upload-status]'),
    canvasHint: root.querySelector('[data-canvas-hint]'),
    canvasPlaceholder: root.querySelector('[data-canvas-placeholder]'),
    colorNote: root.querySelector('[data-color-note]'),
    swatchRoot: root.querySelector('[data-swatch]'),
    swatchTrigger: root.querySelector('[data-swatch-trigger]'),
    swatchList: root.querySelector('[data-swatch-list]'),
    swatchChip: root.querySelector('[data-swatch-chip]'),
    swatchName: root.querySelector('[data-swatch-name]'),
    gridNote: root.querySelector('[data-grid-note]'),
    error: root.querySelector('[data-error]'),
    submit: root.querySelector('[data-submit]'),
    hidden: root.querySelector('[data-hidden-fields]'),
    scaleNote: root.querySelector('[data-scale-note]'),
    saveBtn: root.querySelector('[data-save-quote]'),
    saveStatus: root.querySelector('[data-save-status]'),
    colorField: root.querySelector('[data-color-field]'),
    mcField: root.querySelector('[data-multicolor]'),
    mcRows: root.querySelector('[data-multicolor-rows]'),
    mcNote: root.querySelector('[data-multicolor-note]'),
    layReset: root.querySelector('[data-lay-reset]')
  };

  var state = {
    volume: 0, bbox: null, triangles: 0, fileName: '', price: 0, printer: null,
    file: null, uploads: null, multicolor: null, mcColors: []
  };

  // Multicolor pricing knobs (theme editor). Defaults keep working if the
  // section predates these settings.
  var MC = {
    useSlicer: cfg.mc_use_slicer !== false,
    wasteG: Number(cfg.mc_waste_g) || 8,
    timePct: (Number(cfg.mc_time_pct) || 25) / 100,
    setupFee: cfg.mc_setup_fee != null ? Number(cfg.mc_setup_fee) : 1,
    markupPct: (cfg.mc_markup_pct != null ? Number(cfg.mc_markup_pct) : 15) / 100
  };

  // Round every quoted unit price up to this increment (0 = off).
  var PRICE_STEP = cfg.price_round_up != null ? Number(cfg.price_round_up) : 0.25;

  // Extra copies that share a build plate with the first are discounted.
  var QTY_DISCOUNT = (cfg.qty_discount_pct != null ? Number(cfg.qty_discount_pct) : 50) / 100;
  var PLATE_GAP_MM = 5; // spacing between parts on the plate

  function roundUpPrice(v) {
    return PRICE_STEP > 0 ? Math.ceil(v / PRICE_STEP - 1e-9) * PRICE_STEP : v;
  }

  /* --- STL parsing ------------------------------------------------- */

  function isBinary(buffer) {
    // A binary STL is exactly 84 + 50n bytes. ASCII files almost never
    // land on that number, which is a far more reliable test than
    // sniffing for a leading "solid".
    var view = new DataView(buffer);
    if (buffer.byteLength < 84) return false;
    var n = view.getUint32(80, true);
    return buffer.byteLength === 84 + n * 50;
  }

  function parseBinary(buffer) {
    var view = new DataView(buffer);
    var n = view.getUint32(80, true);
    var tris = new Float32Array(n * 9);
    var offset = 84;

    for (var i = 0; i < n; i++) {
      offset += 12; // skip the stored normal; we recompute it
      for (var v = 0; v < 9; v++) {
        tris[i * 9 + v] = view.getFloat32(offset, true);
        offset += 4;
      }
      offset += 2; // attribute byte count
    }
    return { tris: tris, count: n };
  }

  function parseAscii(text) {
    var verts = [];
    var re = /vertex\s+(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)/g;
    var m;
    while ((m = re.exec(text)) !== null) {
      verts.push(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]));
    }
    return { tris: new Float32Array(verts), count: verts.length / 9 };
  }

  function parseStl(buffer) {
    if (isBinary(buffer)) return parseBinary(buffer);
    return parseAscii(new TextDecoder().decode(buffer));
  }

  /* --- 3MF parsing ------------------------------------------------- */

  // 3MF is a ZIP (OPC) package wrapping an XML mesh model. We pull the
  // 3D model part out of the archive, walk every build item (resolving
  // component references and their transforms), and hand back the same
  // flat triangle array the STL path produces -- in millimetres, so the
  // rest of the tool doesn't need to know which format it came from.

  function inflateZipEntry(bytes, method) {
    if (method === 0) return Promise.resolve(bytes); // stored
    if (method !== 8 || typeof DecompressionStream === 'undefined') {
      return Promise.reject(new Error('unsupported zip compression'));
    }
    var stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    return new Response(stream).arrayBuffer().then(function (buf) {
      return new Uint8Array(buf);
    });
  }

  function readZip(buffer) {
    var view = new DataView(buffer);
    var bytes = new Uint8Array(buffer);
    var len = buffer.byteLength;

    // End Of Central Directory record, scanned from the tail (it may be
    // followed by up to 64 KB of archive comment).
    var eocd = -1;
    for (var i = len - 22; i >= 0 && i >= len - 22 - 65536; i--) {
      if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd < 0) throw new Error('not a zip');

    var count = view.getUint16(eocd + 10, true);
    var p = view.getUint32(eocd + 16, true);
    var entries = {};

    for (var e = 0; e < count; e++) {
      if (view.getUint32(p, true) !== 0x02014b50) break;
      var method = view.getUint16(p + 10, true);
      var compSize = view.getUint32(p + 20, true);
      var nameLen = view.getUint16(p + 28, true);
      var extraLen = view.getUint16(p + 30, true);
      var commentLen = view.getUint16(p + 32, true);
      var localOffset = view.getUint32(p + 42, true);
      var name = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLen));
      entries[name.toLowerCase()] = { method: method, compSize: compSize, localOffset: localOffset };
      p += 46 + nameLen + extraLen + commentLen;
    }
    return { view: view, bytes: bytes, entries: entries };
  }

  function extractZipEntry(zip, entry) {
    var lh = entry.localOffset;
    if (zip.view.getUint32(lh, true) !== 0x04034b50) throw new Error('bad local header');
    var nameLen = zip.view.getUint16(lh + 26, true);
    var extraLen = zip.view.getUint16(lh + 28, true);
    var start = lh + 30 + nameLen + extraLen;
    return inflateZipEntry(zip.bytes.subarray(start, start + entry.compSize), entry.method);
  }

  var UNIT_TO_MM = {
    micron: 0.001, millimeter: 1, centimeter: 10, inch: 25.4, foot: 304.8, meter: 1000
  };

  var IDENTITY_4X4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

  // 3MF transforms are 12 values (3x4, row-vector convention). Store as a
  // row-major 4x4 that multiplies a column vector [x y z 1].
  function parse3mfTransform(str) {
    if (!str) return IDENTITY_4X4.slice();
    var v = str.trim().split(/\s+/).map(Number);
    if (v.length !== 12 || v.some(function (n) { return isNaN(n); })) return IDENTITY_4X4.slice();
    return [
      v[0], v[3], v[6], v[9],
      v[1], v[4], v[7], v[10],
      v[2], v[5], v[8], v[11],
      0, 0, 0, 1
    ];
  }

  function mat4Multiply(a, b) {
    var out = new Array(16);
    for (var r = 0; r < 4; r++) {
      for (var c = 0; c < 4; c++) {
        out[r * 4 + c] =
          a[r * 4] * b[c] + a[r * 4 + 1] * b[4 + c] +
          a[r * 4 + 2] * b[8 + c] + a[r * 4 + 3] * b[12 + c];
      }
    }
    return out;
  }

  function mat4Apply(m, x, y, z) {
    return [
      m[0] * x + m[1] * y + m[2] * z + m[3],
      m[4] * x + m[5] * y + m[6] * z + m[7],
      m[8] * x + m[9] * y + m[10] * z + m[11]
    ];
  }

  // Zip key lookup that tolerates a leading slash and case.
  function zipKey(zip, path) {
    if (!path) return null;
    var p = String(path).replace(/^\/+/, '').toLowerCase();
    return zip.entries[p] ? p : null;
  }

  function textOf(zip, key) {
    if (!key || !zip.entries[key]) return Promise.resolve('');
    return extractZipEntry(zip, zip.entries[key]).then(function (b) {
      return new TextDecoder().decode(b);
    }).catch(function () { return ''; });
  }

  function parseXml(text) {
    var doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) throw new Error('bad xml');
    return doc;
  }

  function splitList(str) {
    return String(str || '').split(/[;,]/).map(function (s) { return s.trim(); }).filter(Boolean);
  }

  // Slicer project files carry the filament list and, when saved after
  // slicing, the real per-filament material usage. Two families:
  //   Bambu Studio / OrcaSlicer -> Metadata/project_settings.config (JSON),
  //     slice_info.config, model_settings.config; painting via paint_color.
  //   PrusaSlicer / SuperSlicer  -> Metadata/Slic3r_PE.config (INI),
  //     Slic3r_PE_model.config (per-volume <extruder>); painting via
  //     mmu_segmentation.
  function read3mfProject(zip, paint) {
    return Promise.all([
      textOf(zip, zipKey(zip, 'Metadata/project_settings.config')),
      textOf(zip, zipKey(zip, 'Metadata/slice_info.config')),
      textOf(zip, zipKey(zip, 'Metadata/model_settings.config')),
      textOf(zip, zipKey(zip, 'Metadata/Slic3r_PE.config')),
      textOf(zip, zipKey(zip, 'Metadata/Slic3r_PE_model.config'))
    ]).then(function (r) {
      var bambuJson = r[0], sliceXml = r[1], bambuModel = r[2], prusaIni = r[3], prusaModel = r[4];
      var colors = [], types = [];

      // Bambu / Orca: project_settings.config is JSON.
      try {
        var ps = JSON.parse(bambuJson || '{}');
        if (Array.isArray(ps.filament_colour)) colors = ps.filament_colour.map(String);
        if (Array.isArray(ps.filament_type)) types = ps.filament_type.map(String);
      } catch (e) { /* not JSON */ }

      // PrusaSlicer / SuperSlicer: Slic3r_PE.config is "; key = value" INI.
      if (prusaIni && !colors.length) {
        var ini = {};
        prusaIni.split(/\r?\n/).forEach(function (line) {
          var m = line.replace(/^;\s*/, '').match(/^([a-z0-9_]+)\s*=\s*(.*)$/i);
          if (m) ini[m[1].toLowerCase()] = m[2];
        });
        // extruder_colour overrides filament_colour for display when set.
        var ec = splitList(ini.extruder_colour);
        colors = ec.length ? ec : splitList(ini.filament_colour);
        types = splitList(ini.filament_type);
      }

      // slice_info.config (Bambu): <filament id="1" used_g="12.3" .../> plus
      // a <metadata key="prediction" value="seconds"> print-time estimate.
      var slicerG = [], printSeconds = 0;
      if (sliceXml) {
        var reF = /<filament\b[^>]*\bused_g="([\d.]+)"/g, mf;
        while ((mf = reF.exec(sliceXml)) !== null) slicerG.push(parseFloat(mf[1]) || 0);
        var reP = /key="prediction"\s+value="([\d.]+)"/.exec(sliceXml);
        if (reP) printSeconds = parseFloat(reP[1]) || 0;
      }

      // Distinct "extruder" values assigned to whole objects / volumes.
      var extruders = {};
      [bambuModel, prusaModel].forEach(function (x) {
        if (!x) return;
        var re = /key="extruder"\s+value="(\d+)"/g, mm;
        while ((mm = re.exec(x)) !== null) extruders[mm[1]] = 1;
      });

      var slicerUsed = slicerG.filter(function (g) { return g > 0; }).length;
      var extruderCount = Object.keys(extruders).length;

      // Painted faces: map each distinct token to a colour slot, ordered
      // by the token's value (slicer paint tokens grow with extruder no.).
      var paintToks = Object.keys(paint).sort(function (a, b) {
        return (parseInt(a, 16) || 0) - (parseInt(b, 16) || 0);
      });
      var tokenIndex = {};
      paintToks.forEach(function (t, i) { tokenIndex[t] = i + 1; });
      var paintCount = paintToks.length ? paintToks.length + 1 : 0;

      var volumes = parseSlic3rVolumes(prusaModel);

      var count = 0;
      if (slicerUsed >= 2) count = slicerUsed;
      else count = Math.max(extruderCount, paintCount);

      if (count < 2) return { mc: null, volumes: volumes, tokenIndex: tokenIndex };
      count = Math.max(2, Math.min(count, 8));

      // Clamp paint slots into range.
      Object.keys(tokenIndex).forEach(function (t) {
        tokenIndex[t] = Math.min(tokenIndex[t], count - 1);
      });

      return {
        mc: {
          count: count,
          colors: colors.slice(0, count),
          types: types.slice(0, count),
          sliced: slicerUsed >= 2,
          total_g: slicerUsed >= 2 ? slicerG.reduce(function (a, g) { return a + g; }, 0) : null,
          print_seconds: printSeconds || 0
        },
        volumes: volumes,
        tokenIndex: tokenIndex
      };
    });
  }

  var NUM = '(-?[\\d.eE+-]+)';

  // Pull every object's geometry out of a model part with regex. Far
  // faster than walking a DOM of hundreds of thousands of vertex nodes,
  // and slicer exports are perfectly regular. Returns
  // { id: { verts, tris, paints } } where paints[i] is the per-triangle
  // paint/segmentation token (or '') for painted multicolor files.
  function extractGeometry(text) {
    var geom = {};
    var objRe = /<object\b[^>]*\bid="([^"]+)"[^>]*>([\s\S]*?)<\/object>/g;
    var om;
    while ((om = objRe.exec(text)) !== null) {
      var body = om[2];
      var vBlock = /<vertices(?:\s[^>]*)?>([\s\S]*?)<\/vertices>/.exec(body);
      var tBlock = /<triangles(?:\s[^>]*)?>([\s\S]*?)<\/triangles>/.exec(body);
      if (!vBlock || !tBlock) continue;

      var verts = [];
      var vRe = new RegExp('x="' + NUM + '"\\s+y="' + NUM + '"\\s+z="' + NUM + '"', 'g');
      var vm;
      while ((vm = vRe.exec(vBlock[1])) !== null) verts.push(+vm[1], +vm[2], +vm[3]);

      var tris = [];
      var paints = [];
      var painted = false;
      var tRe = /v1="(\d+)"\s+v2="(\d+)"\s+v3="(\d+)"([^>]*)/g;
      var tm;
      while ((tm = tRe.exec(tBlock[1])) !== null) {
        tris.push(+tm[1], +tm[2], +tm[3]);
        var pk = /(?:paint_color|mmu_segmentation)="([^"]*)"/.exec(tm[4]);
        var tok = pk && pk[1] && pk[1] !== '0' ? pk[1].charAt(0) : '';
        paints.push(tok);
        if (tok) painted = true;
      }

      geom[om[1]] = {
        verts: new Float64Array(verts),
        tris: tris,
        paints: painted ? paints : null
      };
    }
    return geom;
  }

  // PrusaSlicer / SuperSlicer split a model into colour "volumes" by
  // triangle-index range, each with an extruder. Returns
  // { objectId: [ { first, last, ext } ] }.
  function parseSlic3rVolumes(xml) {
    var out = {};
    if (!xml) return out;
    var objRe = /<object\b[^>]*\bid="([^"]+)"[^>]*>([\s\S]*?)<\/object>/g;
    var om;
    while ((om = objRe.exec(xml)) !== null) {
      var list = [];
      var vRe = /<volume\b[^>]*\bfirstid="(\d+)"[^>]*\blastid="(\d+)"[^>]*>([\s\S]*?)<\/volume>/g;
      var vm;
      while ((vm = vRe.exec(om[2])) !== null) {
        var ext = /key="extruder"\s+value="(\d+)"/.exec(vm[3]);
        list.push({ first: +vm[1], last: +vm[2], ext: ext ? +ext[1] : 1 });
      }
      if (list.length) out[om[1]] = list;
    }
    return out;
  }

  function parse3mf(buffer) {
    var zip;
    try {
      zip = readZip(buffer);
    } catch (err) {
      return Promise.reject(err);
    }

    var modelKeys = Object.keys(zip.entries).filter(function (k) { return /\.model$/.test(k); });
    var rootKey = modelKeys.indexOf('3d/3dmodel.model') !== -1 ? '3d/3dmodel.model' : modelKeys[0];
    if (!rootKey) return Promise.reject(new Error('no 3D model part'));

    var docs = {};
    var paint = {}; // leading char of every paint_color / mmu_segmentation value

    function loadPart(key) {
      if (docs[key]) return Promise.resolve(docs[key]);
      return extractZipEntry(zip, zip.entries[key]).then(function (b) {
        var text = new TextDecoder().decode(b);

        var pRe = /(?:paint_color|mmu_segmentation)="([^"\s]+)"/g, pm;
        while ((pm = pRe.exec(text)) !== null) {
          if (pm[1] && pm[1] !== '0') paint[pm[1].charAt(0)] = 1;
        }

        var geom = extractGeometry(text);

        // Strip the heavy geometry, then DOM-parse only the structure
        // (objects, components, build items, unit).
        var light = text.replace(/<(vertices|triangles)(?:\s[^>]*)?>[\s\S]*?<\/\1>/g, '');
        var doc = parseXml(light);
        var objects = {};
        var objEls = doc.getElementsByTagName('object');
        for (var i = 0; i < objEls.length; i++) {
          var oid = objEls[i].getAttribute('id');
          if (oid != null) objects[oid] = objEls[i];
        }
        docs[key] = {
          objects: objects,
          geom: geom,
          items: doc.getElementsByTagName('item'),
          unit: (doc.documentElement.getAttribute('unit') || '').toLowerCase()
        };
        return docs[key];
      });
    }

    function itemPath(el) {
      var attrs = el.attributes;
      for (var i = 0; i < attrs.length; i++) {
        if (attrs[i].localName === 'path' || attrs[i].name === 'p:path') return attrs[i].value;
      }
      return null;
    }

    return loadPart(rootKey).then(function (root) {
      var scale = UNIT_TO_MM[root.unit || 'millimeter'] || 1;

      var items = root.items;
      var external = [];
      for (var b = 0; b < items.length; b++) {
        var pth = itemPath(items[b]);
        var k = pth && zipKey(zip, pth);
        if (k && !docs[k]) external.push(k);
      }

      return Promise.all(external.map(loadPart))
        .then(function () { return read3mfProject(zip, paint); })
        .then(function (proj) {
          var mc = proj.mc;
          var volumes = proj.volumes;
          var tokenIndex = proj.tokenIndex;
          var out = [];
          var regions = mc ? [] : null;

          // Per-object triangle -> colour-slot lookup, from volume ranges
          // (PrusaSlicer) then paint tokens (both slicers). Built lazily.
          var regionCache = {};
          function regionMap(id, g) {
            if (regionCache[id] !== undefined) return regionCache[id];
            var n = g.tris.length / 3;
            var vols = volumes[id];
            var map = null;
            if (vols) {
              map = new Uint8Array(n);
              vols.forEach(function (vv) {
                var r = Math.min(Math.max(vv.ext - 1, 0), mc.count - 1);
                for (var x = vv.first; x <= vv.last && x < n; x++) map[x] = r;
              });
            } else if (g.paints) {
              map = new Uint8Array(n);
              for (var i = 0; i < n; i++) {
                map[i] = tokenIndex[g.paints[i]] || 0;
              }
            }
            regionCache[id] = map;
            return map;
          }

          function emitFrom(part, id, mat, depth) {
            if (!part || depth > 12) return;
            var g = part.geom[id] || root.geom[id];
            if (g) {
              var v = g.verts, tri = g.tris;
              var rmap = regions ? regionMap(id, g) : null;
              for (var ti = 0; ti < tri.length; ti += 3) {
                for (var n = 0; n < 3; n++) {
                  var vi = tri[ti + n] * 3;
                  var pt = mat4Apply(mat, v[vi], v[vi + 1], v[vi + 2]);
                  out.push(pt[0], pt[1], pt[2]);
                }
                if (regions) regions.push(rmap ? rmap[ti / 3] : 0);
              }
            }

            var el = part.objects[id] || root.objects[id];
            if (!el) return;
            var components = null;
            for (var c = 0; c < el.children.length; c++) {
              if (el.children[c].localName === 'components') components = el.children[c];
            }
            if (components) {
              var compEls = components.getElementsByTagName('component');
              for (var q = 0; q < compEls.length; q++) {
                var cp = itemPath(compEls[q]);
                var cpKey = cp && zipKey(zip, cp);
                emitFrom(
                  (cpKey && docs[cpKey]) ? docs[cpKey] : part,
                  compEls[q].getAttribute('objectid'),
                  mat4Multiply(mat, parse3mfTransform(compEls[q].getAttribute('transform'))),
                  depth + 1
                );
              }
            }
          }

          if (items.length) {
            for (var b2 = 0; b2 < items.length; b2++) {
              var it = items[b2];
              var ipath = itemPath(it);
              var ikey = ipath && zipKey(zip, ipath);
              emitFrom((ikey && docs[ikey]) ? docs[ikey] : root,
                it.getAttribute('objectid'),
                parse3mfTransform(it.getAttribute('transform')), 0);
            }
          } else {
            Object.keys(root.geom).forEach(function (id) {
              emitFrom(root, id, IDENTITY_4X4.slice(), 0);
            });
          }

          if (scale !== 1) {
            for (var s = 0; s < out.length; s++) out[s] *= scale;
          }

          return {
            tris: new Float32Array(out),
            count: (out.length / 9) | 0,
            multicolor: mc,
            regions: regions ? new Uint8Array(regions) : null
          };
        });
    });
  }

  /* --- Geometry ----------------------------------------------------- */

  function measure(tris, count) {
    var vol = 0;
    var min = [Infinity, Infinity, Infinity];
    var max = [-Infinity, -Infinity, -Infinity];

    for (var i = 0; i < count; i++) {
      var o = i * 9;
      var ax = tris[o],     ay = tris[o + 1], az = tris[o + 2];
      var bx = tris[o + 3], by = tris[o + 4], bz = tris[o + 5];
      var cx = tris[o + 6], cy = tris[o + 7], cz = tris[o + 8];

      // Signed volume of the tetrahedron from the origin to this face.
      // Summed over a closed mesh this gives the enclosed volume.
      vol += (
        ax * (by * cz - bz * cy) -
        ay * (bx * cz - bz * cx) +
        az * (bx * cy - by * cx)
      ) / 6;

      for (var k = 0; k < 3; k++) {
        var p = [tris[o + k * 3], tris[o + k * 3 + 1], tris[o + k * 3 + 2]];
        for (var d = 0; d < 3; d++) {
          if (p[d] < min[d]) min[d] = p[d];
          if (p[d] > max[d]) max[d] = p[d];
        }
      }
    }

    return {
      // STL units are conventionally millimetres. mm^3 -> cm^3.
      volume: Math.abs(vol) / 1000,
      bbox: {
        x: max[0] - min[0],
        y: max[1] - min[1],
        z: max[2] - min[2],
        min: min,
        max: max
      }
    };
  }

  /* --- Printer selection -------------------------------------------- */

  // A part that is too long on one axis may still fit turned on its
  // side, so compare sorted dimensions against the sorted build volume.
  // This covers every axis-aligned orientation. It ignores diagonal
  // placement, which slightly understates capacity -- erring toward
  // quoting the larger machine rather than promising a print we can't do.
  function fits(dims, printer) {
    var d = dims.slice().sort(function (a, b) { return b - a; });
    var p = [printer.x, printer.y, printer.z].sort(function (a, b) { return b - a; });
    return d[0] <= p[0] && d[1] <= p[1] && d[2] <= p[2];
  }

  // How many copies fit on one plate of this printer. Parts are laid on
  // their largest face (shortest side up, same as the preview), then
  // gridded with a gap between them, trying both turns on the bed. Grid
  // packing understates what a real slicer can nest, which errs toward
  // fewer discounted copies rather than promising a fit we can't deliver.
  function copiesPerPlate(bbox, printer) {
    var d = [bbox.x, bbox.y, bbox.z].sort(function (p, q) { return p - q; });
    if (d[0] > printer.z) return 1;
    var a = d[1], b = d[2];
    var best = 1;
    [[a, b], [b, a]].forEach(function (f) {
      var nx = Math.floor((printer.x + PLATE_GAP_MM) / (f[0] + PLATE_GAP_MM));
      var ny = Math.floor((printer.y + PLATE_GAP_MM) / (f[1] + PLATE_GAP_MM));
      if (nx * ny > best) best = nx * ny;
    });
    return best;
  }

  function choosePrinter(bbox) {
    var dims = [bbox.x, bbox.y, bbox.z];
    for (var i = 0; i < cfg.printers.length; i++) {
      if (fits(dims, cfg.printers[i])) return cfg.printers[i];
    }
    return null;
  }

  /* --- Pricing ------------------------------------------------------ */

  function price() {
    if (!state.volume || !state.printer) return null;

    var mat = cfg.materials[els.material.value];
    if (!mat) return null;

    var infill = parseInt(els.infill.value, 10) / 100;
    var quality = cfg.qualities[els.quality.value] || { mult: 1, speed: 1 };
    var qty = Math.max(1, parseInt(els.qty.value, 10) || 1);

    // Perimeters and top/bottom layers consume material regardless of
    // infill, so a part is never as light as its infill percentage
    // implies. cfg.shell_fraction is the floor.
    var shell = cfg.shell_fraction;
    var effective = state.volume * (shell + (1 - shell) * infill);

    var grams = effective * mat.density;

    // Time scales with deposited material, not bounding box. Rough, but
    // it tracks reality better than volume alone.
    var hours = (effective * cfg.hours_per_cm3) / quality.speed;

    // Multicolor: filament swaps purge material into a waste tower and add
    // print time. If the customer saved a sliced project we have the
    // slicer's real totals (flush included); otherwise estimate.
    var mc = state.multicolor;
    var mcSetup = 0;
    var mcColorPremium = 0;
    if (mc) {
      if (MC.useSlicer && mc.sliced && mc.total_g) {
        grams = mc.total_g;
        hours = mc.print_seconds ? mc.print_seconds / 3600 : hours * (1 + MC.timePct);
      } else {
        grams = grams + Math.max(0, mc.count - 1) * MC.wasteG;
        hours = hours * (1 + MC.timePct);
      }
      mcSetup = Math.max(0, mc.count - 1) * MC.setupFee;
      (state.mcColors || []).forEach(function (key) {
        var pc = cfg.palette[key];
        if (pc && pc.premium) mcColorPremium += Number(pc.premium);
      });
    }

    var materialCost = grams * mat.price_per_gram;
    var machineCost = hours * cfg.hourly_rate;

    var each = (materialCost + machineCost) * quality.mult;
    each = each * (1 + cfg.margin) + cfg.setup_fee;

    // Oversized parts tie up the large-format machine for longer and
    // can't be batched alongside other jobs, so they carry a surcharge.
    var surcharge = state.printer.surcharge || 0;
    each = each * (1 + surcharge);

    if (each < cfg.minimum_price) each = cfg.minimum_price;

    // Applied after the floor: glow filament costs more to buy, so the
    // surcharge should survive on small parts rather than being
    // swallowed by the minimum.
    var col = currentColor();
    var premium = mc ? mcColorPremium : (col && col.premium ? Number(col.premium) : 0);
    each = each + premium + mcSetup;

    // Flat multicolor markup on top of everything (swap risk, hand-tuning).
    if (mc && MC.markupPct) each = each * (1 + MC.markupPct);

    // Round the unit price up to the configured increment (e.g. $10.01 -> $10.25).
    each = roundUpPrice(each);

    // Copies that share a plate: the first on each plate is full price,
    // the rest get the quantity discount. A part too big to fit two on a
    // plate gets no discount.
    var perPlate = state.bbox ? copiesPerPlate(state.bbox, state.printer) : 1;
    var plates = Math.ceil(qty / perPlate);
    var extraCopies = qty - plates;
    var eachExtra = extraCopies > 0 ? roundUpPrice(each * (1 - QTY_DISCOUNT)) : each;
    var total = each * plates + eachExtra * extraCopies;

    return {
      each: each,
      eachExtra: eachExtra,
      extraCopies: extraCopies,
      perPlate: perPlate,
      total: total,
      grams: grams,
      hours: hours,
      qty: qty,
      surcharge: surcharge,
      premium: premium,
      mc_setup: mcSetup,
      multicolor: mc ? { count: mc.count, sliced: mc.sliced } : null
    };
  }

  function money(v) {
    return cfg.currency_symbol + v.toFixed(2);
  }

  /* --- Preview ------------------------------------------------------ */

  var view = null;

  // Choose a printing orientation: lay the part so its largest face is
  // on the bed and its tallest dimension is vertical. That's the usual
  // starting point for a real print -- lowest height, best adhesion,
  // least support. Returns Euler angles applied to the model.
  function printOrientation(bbox) {
    var d = [
      { axis: 'x', size: bbox.x },
      { axis: 'y', size: bbox.y },
      { axis: 'z', size: bbox.z }
    ].sort(function (a, b) { return a.size - b.size; });

    // The smallest dimension should point up (Z), so the part lies flat.
    var shortest = d[0].axis;

    if (shortest === 'z') return { x: 0, y: 0, z: 0 };
    if (shortest === 'x') return { x: 0, y: Math.PI / 2, z: 0 };
    return { x: Math.PI / 2, y: 0, z: 0 };
  }

  // Dimensions after the orientation rotation, so the plate is sized and
  // positioned against what the customer actually sees.
  function orientedBounds(bbox, rot) {
    if (rot.y) return { x: bbox.z, y: bbox.y, z: bbox.x };
    if (rot.x) return { x: bbox.x, y: bbox.z, z: bbox.y };
    return { x: bbox.x, y: bbox.y, z: bbox.z };
  }

  // A shadow plane parented to the pivot, so it turns with the model.
  // Because both are children of the same group their relative position
  // is fixed -- the plane can never pass through the part at any angle,
  // which is what happened when the two rotated independently.
  function buildPlate(ob, pivot) {
    if (!view || !pivot) return;

    if (view.plate) {
      if (view.plate.parent) view.plate.parent.remove(view.plate);
      if (view.plate.geometry) view.plate.geometry.dispose();
      if (view.plate.material) view.plate.material.dispose();
      view.plate = null;
    }

    var footprint = Math.max(ob.x, ob.y) || 10;
    var span = footprint * 2.2;

    var plate = new THREE.Mesh(
      new THREE.PlaneGeometry(span, span),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.06,
        depthWrite: false,
        side: THREE.DoubleSide
      })
    );

    // Sit it just clear of the model's underside. The offset scales with
    // the part: a fixed gap is invisible on a 100mm model but looks like
    // hovering on a 1mm-thick one.
    plate.position.z = -Math.max(0.05, ob.z * 0.004);

    // Drawn before the model, and never writing depth, so the part
    // always renders on top of it.
    plate.renderOrder = -1;

    pivot.add(plate);
    view.plate = plate;

    if (els.gridNote) els.gridNote.hidden = true;
  }

  /* --- Click-to-lay-flat ------------------------------------------- */

  // Re-seat the mesh after its orientation changed: drop it so its
  // lowest point rests on the plate at z=0, recentre it over the origin,
  // then resize the plate and re-aim the camera to the new height.
  function seatMeshOnPlate() {
    if (!view || !view.mesh) return;
    var geo = view.mesh.geometry;
    if (!geo.boundingBox) geo.computeBoundingBox();
    var bb = geo.boundingBox;
    var q = view.mesh.quaternion;
    var v = new THREE.Vector3();
    var lo = [Infinity, Infinity, Infinity];
    var hi = [-Infinity, -Infinity, -Infinity];

    for (var i = 0; i < 8; i++) {
      v.set(
        (i & 1) ? bb.max.x : bb.min.x,
        (i & 2) ? bb.max.y : bb.min.y,
        (i & 4) ? bb.max.z : bb.min.z
      ).applyQuaternion(q);
      lo[0] = Math.min(lo[0], v.x); hi[0] = Math.max(hi[0], v.x);
      lo[1] = Math.min(lo[1], v.y); hi[1] = Math.max(hi[1], v.y);
      lo[2] = Math.min(lo[2], v.z); hi[2] = Math.max(hi[2], v.z);
    }

    view.mesh.position.set(-(lo[0] + hi[0]) / 2, -(lo[1] + hi[1]) / 2, -lo[2]);

    var ob = { x: hi[0] - lo[0], y: hi[1] - lo[1], z: hi[2] - lo[2] };
    view.orientedZ = ob.z;
    view.target = ob.z / 2;
    buildPlate(ob, view.pivot);

    var span = Math.max(ob.x, ob.y, ob.z) || 1;
    view.minDist = span * 0.6;
    view.maxDist = span * 8;
    view.camera.lookAt(0, 0, view.target);
    render();
  }

  // Rotate the model so the clicked face lies flat on the plate.
  function layFlatAt(clientX, clientY) {
    if (!view || !view.mesh || typeof THREE === 'undefined') return;
    var rect = els.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1
    );
    var rc = new THREE.Raycaster();
    rc.setFromCamera(ndc, view.camera);
    view.mesh.updateWorldMatrix(true, false);
    var hits = rc.intersectObject(view.mesh, false);
    if (!hits.length || !hits[0].face) return;

    // The clicked face's normal, expressed in the pivot's frame (which is
    // where the plate lives). Rotating this to point straight down puts
    // the face on the bed.
    var n = hits[0].face.normal.clone().applyQuaternion(view.mesh.quaternion).normalize();
    var q = new THREE.Quaternion().setFromUnitVectors(n, new THREE.Vector3(0, 0, -1));
    view.mesh.quaternion.premultiply(q);

    seatMeshOnPlate();

    view.reoriented = true;
    if (els.layReset) els.layReset.hidden = false;
    dismissCanvasHint();
  }

  function resetOrientation() {
    if (!view || !view.mesh || !view.baseQuat) return;
    view.mesh.quaternion.copy(view.baseQuat);
    seatMeshOnPlate();
    view.reoriented = false;
    if (els.layReset) els.layReset.hidden = true;
  }

  if (els.layReset) {
    els.layReset.addEventListener('click', resetOrientation);
  }

  function initPreview(tris, count, bbox) {
    if (!els.canvas) return;

    // Undo any hiding left over from an earlier failed attempt, and reveal
    // it now that there's actually something to preview.
    els.canvas.style.display = '';
    els.canvas.hidden = false;
    if (els.canvasPlaceholder) els.canvasPlaceholder.hidden = true;

    if (typeof THREE === 'undefined') {
      if (window.console) console.error('STL preview: three.js did not load.');
      if (els.canvasHint) {
        els.canvasHint.textContent = 'Preview unavailable \u2014 3D library blocked. Your quote is unaffected.';
        els.canvasHint.hidden = false;
      }
      return;
    }

    els.canvas.setAttribute('tabindex', '0');
    els.canvas.setAttribute('role', 'img');
    els.canvas.setAttribute('aria-label', 'Preview of your model. Use arrow keys to rotate.');

    try {
      if (!view) {
        var renderer = new THREE.WebGLRenderer({
          canvas: els.canvas,
          antialias: true,
          alpha: true,
          // Without this the buffer is cleared after each frame and
          // toBlob() returns a blank image.
          preserveDrawingBuffer: true
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        var scene = new THREE.Scene();
        var camera = new THREE.PerspectiveCamera(40, 1, 0.1, 10000);

        scene.add(new THREE.AmbientLight(0xffffff, 0.65));
        var key = new THREE.DirectionalLight(0xffffff, 0.7);
        key.position.set(1, 1, 1);
        scene.add(key);
        var fill = new THREE.DirectionalLight(0xffffff, 0.25);
        fill.position.set(-1, -0.5, -1);
        scene.add(fill);

        view = { renderer: renderer, scene: scene, camera: camera, mesh: null };
      }

      if (view.pivot) {
        view.scene.remove(view.pivot);
        if (view.mesh) {
          view.mesh.geometry.dispose();
          view.mesh.material.dispose();
        }
        view.pivot = null;
        view.mesh = null;
        view.plate = null;
      }

      var geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(tris.slice(0, count * 9), 3));
      geo.computeVertexNormals();
      geo.center();

      // Multicolor: a per-vertex colour buffer, filled from the region
      // map so each painted area shows its assigned filament.
      var multicolorMesh = !!(state.multicolor && state.regions && state.regions.length === count);
      if (multicolorMesh) {
        geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(count * 9), 3));
      }

      var mesh = new THREE.Mesh(
        geo,
        new THREE.MeshPhongMaterial({
          color: multicolorMesh ? 0xffffff : 0xb0b0b0,
          specular: 0x222222,
          shininess: 20,
          flatShading: false,
          vertexColors: multicolorMesh
        })
      );

      // Orient the part as it would be printed, then lift it so its
      // underside rests exactly on the plate at z=0.
      var rot = printOrientation(bbox);
      mesh.rotation.set(rot.x, rot.y, rot.z);

      var ob = orientedBounds(bbox, rot);
      mesh.position.z = ob.z / 2;

      // The pivot carries the model. User rotation is applied here, so
      // the mesh keeps its print orientation relative to the pivot and
      // the plate below stays level and independent.
      var pivot = new THREE.Group();
      pivot.add(mesh);
      view.scene.add(pivot);

      mesh.renderOrder = 0;

      view.mesh = mesh;
      view.pivot = pivot;
      view.orientedZ = ob.z;
      view.multicolorMesh = multicolorMesh;
      view.baseQuat = mesh.quaternion.clone();
      view.reoriented = false;
      if (els.layReset) els.layReset.hidden = true;

      buildPlate(ob, pivot);

      var span = Math.max(ob.x, ob.y, ob.z) || 1;
      // Look at the model's mid-height, not the plate, so the part sits
      // centred in frame rather than hugging the bottom edge.
      view.target = ob.z / 2;
      view.camera.position.set(span * 1.4, span * 1.4, ob.z / 2 + span * 0.9);
      view.camera.lookAt(0, 0, view.target);
      view.minDist = span * 0.6;
      view.maxDist = span * 8;

      resize();
      bindControls();
      render();

      if (els.canvasHint) {
        // Restore the rotate/zoom hint in case a previous attempt on this
        // page left an error message (or a dismissal) in its place.
        els.canvasHint.innerHTML =
          '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="M3 12a9 9 0 1 0 3.5-7.1"></path><path d="M3 4v5h5"></path>' +
          '</svg>Drag to rotate &middot; scroll to zoom &middot; click a face to lay it flat';
        els.canvasHint.classList.remove('is-dismissed');
        els.canvasHint.hidden = false;
      }
      applyColor();
      render();
    } catch (e) {
      // A failed preview must never block the quote. Log it rather than
      // hiding the canvas -- a silently vanishing viewer is harder to
      // diagnose than an empty one.
      if (window.console) console.error('STL preview failed:', e);
      if (els.canvasHint) {
        els.canvasHint.textContent = 'Preview unavailable in this browser. Your quote is unaffected.';
        els.canvasHint.hidden = false;
      }
    }
  }

  function render() {
    if (view) view.renderer.render(view.scene, view.camera);
  }

  // Hand-rolled orbit: r128 doesn't expose OrbitControls as a global,
  // and dragging plus wheel-zoom is all this preview needs. Renders on
  // interaction rather than looping, so an idle preview costs nothing.
  var drag = { active: false, x: 0, y: 0, pointer: null };
  var controlsBound = false;

  // Once the customer has rotated or zoomed once, they've found the
  // controls - fade the hint out instead of leaving it in the way.
  function dismissCanvasHint() {
    if (els.canvasHint && !els.canvasHint.hidden && !els.canvasHint.classList.contains('is-dismissed')) {
      els.canvasHint.classList.add('is-dismissed');
    }
  }

  function bindControls() {
    if (controlsBound || !els.canvas) return;
    controlsBound = true;

    els.canvas.addEventListener('pointerdown', function (e) {
      if (!view || !view.pivot) return;
      drag.active = true;
      drag.x = e.clientX;
      drag.y = e.clientY;
      drag.startX = e.clientX;
      drag.startY = e.clientY;
      drag.moved = false;
      drag.pointer = e.pointerId;
      els.canvas.classList.add('is-grabbing');
      try { els.canvas.setPointerCapture(e.pointerId); } catch (err) {}
      dismissCanvasHint();
    });

    els.canvas.addEventListener('pointermove', function (e) {
      if (!drag.active || !view || !view.pivot) return;
      var dx = e.clientX - drag.x;
      var dy = e.clientY - drag.y;
      drag.x = e.clientX;
      drag.y = e.clientY;
      if (Math.abs(e.clientX - drag.startX) + Math.abs(e.clientY - drag.startY) > 4) {
        drag.moved = true;
      }

      // STL models are conventionally Z-up, so horizontal drag spins
      // about Z and vertical drag tilts about X.
      // Spin the pivot, not the mesh: the model keeps its print
      // orientation and the plate below stays level.
      view.pivot.rotation.z += dx * 0.01;
      view.pivot.rotation.x += dy * 0.01;
      render();
    });

    function endDrag(e) {
      if (!drag.active) return;
      drag.active = false;
      els.canvas.classList.remove('is-grabbing');
      try { els.canvas.releasePointerCapture(drag.pointer); } catch (err) {}
    }
    els.canvas.addEventListener('pointerup', function (e) {
      var wasClick = drag.active && !drag.moved;
      endDrag(e);
      // A click (no drag) on the model lays that face flat on the plate.
      if (wasClick) layFlatAt(e.clientX, e.clientY);
    });
    els.canvas.addEventListener('pointercancel', endDrag);

    els.canvas.addEventListener('wheel', function (e) {
      if (!view) return;
      e.preventDefault();
      dismissCanvasHint();
      var factor = e.deltaY > 0 ? 1.1 : 0.9;
      var p = view.camera.position;
      var dist = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
      var next = dist * factor;
      // Keep the model from being zoomed inside-out or lost to infinity.
      if (next < view.minDist || next > view.maxDist) return;
      p.multiplyScalar(factor);
      view.camera.lookAt(0, 0, view.target || 0);
      render();
    }, { passive: false });

    // Keyboard equivalent, so the preview isn't mouse-only.
    els.canvas.addEventListener('keydown', function (e) {
      if (!view || !view.pivot) return;
      var step = 0.15;
      if (e.key === 'ArrowLeft') view.pivot.rotation.z -= step;
      else if (e.key === 'ArrowRight') view.pivot.rotation.z += step;
      else if (e.key === 'ArrowUp') view.pivot.rotation.x -= step;
      else if (e.key === 'ArrowDown') view.pivot.rotation.x += step;
      else return;
      e.preventDefault();
      render();
    });
  }

  function resize() {
    if (!view || !els.canvas) return;
    var w = els.canvas.clientWidth || 400;
    var h = els.canvas.clientHeight || 300;
    view.renderer.setSize(w, h, false);
    view.camera.aspect = w / h;
    view.camera.updateProjectionMatrix();
    render();
  }
  window.addEventListener('resize', resize);

  /* --- Preview capture ---------------------------------------------- */

  // Snapshot the rendered preview into a second file input so the order
  // carries a picture of what was quoted, not just the raw mesh. Runs
  // once a frame has actually been drawn.
  function capturePreview(done) {
    if (!view || !els.canvas || typeof els.canvas.toBlob !== 'function') {
      done(null);
      return;
    }

    try {
      view.renderer.render(view.scene, view.camera);

      els.canvas.toBlob(function (blob) {
        done(blob || null);
      }, 'image/png');
    } catch (e) { done(null); }
  }

  /* --- Uploading ------------------------------------------------------ */

  // Files go straight from the browser to Shopify's storage using a
  // signed target the Worker fetches for us. The Worker only ever sees
  // small JSON, and the cart carries URLs rather than file inputs -- so
  // the attachments survive Shop Pay and other express checkouts.

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body.error || 'Upload failed');
        return body;
      });
    });
  }

  function uploadOne(blob, filename, kind) {
    var base = cfg.worker_url.replace(/\/$/, '');

    return post(base + '/upload/start', {
      filename: filename,
      mimeType: blob.type || 'application/octet-stream',
      fileSize: blob.size
    }).then(function (target) {
      var form = new FormData();
      target.parameters.forEach(function (p) {
        form.append(p.name, p.value);
      });
      form.append('file', blob, filename);

      return fetch(target.uploadUrl, { method: 'POST', body: form })
        .then(function (res) {
          if (!res.ok) throw new Error('Storage rejected the upload');
          return post(base + '/upload/complete', {
            resourceUrl: target.resourceUrl,
            storedName: target.storedName,
            kind: kind
          });
        });
    });
  }

  function setUploadState(msg, ready) {
    if (els.uploadStatus) {
      els.uploadStatus.textContent = msg || '';
      els.uploadStatus.hidden = !msg;
    }
    if (els.submit) els.submit.disabled = !ready;
  }

  function uploadAssets(previewBlob) {
    if (!cfg.worker_url || !state.file) return;

    state.uploads = null;
    setUploadState('Uploading your file\u2026', false);

    var jobs = [uploadOne(state.file, state.fileName, 'file')];

    if (previewBlob) {
      var stem = state.fileName.replace(/\.(stl|3mf)$/i, '') || 'model';
      jobs.push(uploadOne(previewBlob, stem + '-preview.png', 'image'));
    }

    Promise.all(jobs).then(function (results) {
      state.uploads = { stl: results[0], preview: results[1] || null };

      if (els.stlUrl) els.stlUrl.value = results[0].url || '';
      if (els.previewUrl && results[1]) els.previewUrl.value = results[1].url || '';
      if (els.fileIds) {
        els.fileIds.value = results
          .filter(Boolean)
          .map(function (r) { return r.id; })
          .join(',');
      }

      setUploadState('', true);
      update();
    }).catch(function (err) {
      // A failed upload must not let someone check out with no model
      // attached -- better to block and say so.
      setUploadState(err.message || 'Upload failed. Please try again.', false);
    });
  }

  /* --- Color handling ------------------------------------------------ */

  // Each material carries its own color list, so switching material
  // rebuilds the picker. The previous choice is kept when the new
  // material also offers it.
  function populateColors() {
    if (!els.color) return;

    var mat = cfg.materials[els.material.value];
    if (!mat || !mat.colors) return;

    var previous = els.color.value;
    els.color.innerHTML = '';

    mat.colors.forEach(function (key) {
      var c = cfg.palette[key];
      if (!c) return;
      var opt = document.createElement('option');
      opt.value = key;
      opt.textContent = c.premium > 0
        ? c.label + ' (+' + cfg.currency_symbol + Number(c.premium).toFixed(2) + ')'
        : c.label;
      els.color.appendChild(opt);
    });

    if (mat.colors.indexOf(previous) !== -1) {
      els.color.value = previous;
    }

    bindSwatch();
    buildSwatchList();
    if (state.multicolor) buildMulticolorRows();
    applyColor();
  }

  function currentColor() {
    if (!els.color) return null;
    return cfg.palette[els.color.value] || null;
  }

  /* --- Multicolor (painted 3MF) ------------------------------------- */

  function hexToRgb(h) {
    h = String(h || '').replace(/^#/, '');
    if (h.length === 8) h = h.slice(0, 6);
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  // Closest shop filament to a colour pulled from the file.
  function nearestPaletteKey(hex, keys) {
    var target = hexToRgb(hex);
    if (!target || !keys.length) return keys[0];
    var best = keys[0], bestD = Infinity;
    keys.forEach(function (key) {
      var c = cfg.palette[key];
      var rgb = c && hexToRgb(c.hex);
      if (!rgb) return;
      var d = (rgb[0] - target[0]) * (rgb[0] - target[0]) +
              (rgb[1] - target[1]) * (rgb[1] - target[1]) +
              (rgb[2] - target[2]) * (rgb[2] - target[2]);
      if (d < bestD) { bestD = d; best = key; }
    });
    return best;
  }

  function mcColorText() {
    return (state.mcColors || []).map(function (key) {
      var c = cfg.palette[key];
      return c ? c.label : key;
    }).join(', ');
  }

  // Swatch fill for a chosen filament, so each row's chip is a legend for
  // the region it controls in the preview.
  function mcChipStyle(key) {
    var c = cfg.palette[key];
    if (!c) return 'background:#cccccc';
    if (c.opacity) {
      return 'background:linear-gradient(135deg,rgba(255,255,255,.9),rgba(180,200,210,.55))';
    }
    var s = 'background:' + c.hex;
    if (c.glow) s += ';box-shadow:inset 0 0 0 1px rgba(0,0,0,.25),0 0 6px 1px ' + c.glow + '66';
    return s;
  }

  function buildMulticolorRows() {
    if (!els.mcRows || !state.multicolor) return;
    var mat = cfg.materials[els.material.value];
    var keys = (mat && mat.colors) || [];
    var src = state.multicolor.colors || [];
    var n = state.multicolor.count;
    var keep = (state.pendingMcColors && state.pendingMcColors.length)
      ? state.pendingMcColors.slice()
      : state.mcColors.slice();
    state.pendingMcColors = null;

    els.mcRows.innerHTML = '';
    state.mcColors = [];

    for (var i = 0; i < n; i++) {
      var row = document.createElement('div');
      row.className = 'quote__mc-row';

      var chip = document.createElement('span');
      chip.className = 'quote__mc-chip';

      var label = document.createElement('span');
      label.className = 'quote__mc-label';
      label.textContent = 'Color ' + (i + 1);

      var sel = document.createElement('select');
      sel.className = 'quote__mc-select';
      sel.setAttribute('data-mc-index', String(i));
      keys.forEach(function (key) {
        var c = cfg.palette[key];
        if (!c) return;
        var opt = document.createElement('option');
        opt.value = key;
        opt.textContent = c.premium > 0
          ? c.label + ' (+' + cfg.currency_symbol + Number(c.premium).toFixed(2) + ')'
          : c.label;
        sel.appendChild(opt);
      });

      var choice = (keep[i] && keys.indexOf(keep[i]) !== -1) ? keep[i] : nearestPaletteKey(src[i], keys);
      sel.value = choice;
      state.mcColors.push(choice);
      chip.setAttribute('style', mcChipStyle(choice));

      sel.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-mc-index'), 10);
        state.mcColors[idx] = this.value;
        var ch = this.parentNode && this.parentNode.querySelector('.quote__mc-chip');
        if (ch) ch.setAttribute('style', mcChipStyle(this.value));
        applyColor();
        update();
      });

      row.appendChild(chip);
      row.appendChild(label);
      row.appendChild(sel);
      els.mcRows.appendChild(row);
    }
  }

  function applyMulticolorUI() {
    var mc = state.multicolor;
    if (els.mcField) els.mcField.hidden = !mc;
    if (els.colorField) els.colorField.hidden = !!mc;

    if (!mc) { state.mcColors = []; return; }

    buildMulticolorRows();

    if (els.mcNote) {
      var bits = [mc.count + ' colors detected in this file'];
      if (mc.sliced && mc.total_g) bits.push('slicer estimate ' + Math.round(mc.total_g) + ' g incl. purge waste');
      var mixed = mc.types && mc.types.length > 1 &&
        mc.types.some(function (t) { return t && t.toUpperCase() !== String(mc.types[0]).toUpperCase(); });
      if (mixed) bits.push('the file mixes filament types — every part prints in the one material you pick above');
      bits.push('final price is confirmed after we slice the file');
      els.mcNote.textContent = bits.join(' · ');
      els.mcNote.hidden = false;
    }
  }

  // Paint the multicolor preview: each triangle's vertices get the
  // filament colour picked for its region.
  function recolorPreview() {
    if (!view || !view.mesh || !view.multicolorMesh || !state.regions) return;
    var attr = view.mesh.geometry.getAttribute('color');
    if (!attr) return;

    var rgbBySlot = (state.mcColors || []).map(function (key) {
      var pc = cfg.palette[key];
      var rgb = pc && hexToRgb(pc.hex);
      return rgb ? [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255] : [0.7, 0.7, 0.7];
    });
    var fallback = rgbBySlot[0] || [0.7, 0.7, 0.7];

    var regions = state.regions, arr = attr.array;
    for (var t = 0; t < regions.length; t++) {
      var rgb = rgbBySlot[regions[t]] || fallback;
      var o = t * 9;
      for (var k = 0; k < 3; k++) {
        arr[o + k * 3] = rgb[0];
        arr[o + k * 3 + 1] = rgb[1];
        arr[o + k * 3 + 2] = rgb[2];
      }
    }
    attr.needsUpdate = true;
    render();
  }

  // Tint the preview to the selected filament. Transparent stock gets
  // real transparency; glow stock gets an emissive lift so it reads as
  // luminous rather than just pale.
  function applyColor() {
    if (state.multicolor) {
      if (els.colorNote) els.colorNote.hidden = true;
      recolorPreview();
      return;
    }

    var c = currentColor();

    if (els.colorNote) {
      if (c && c.premium > 0) {
        els.colorNote.textContent = 'Glow filament costs more, so this adds ' +
          cfg.currency_symbol + Number(c.premium).toFixed(2) + ' per print.';
        els.colorNote.hidden = false;
      } else if (c && c.opacity) {
        els.colorNote.textContent = 'Translucent rather than fully clear \u2014 layer lines stay visible.';
        els.colorNote.hidden = false;
      } else {
        els.colorNote.hidden = true;
      }
    }

    if (!view || !view.mesh || !c || typeof THREE === 'undefined') return;

    var m = view.mesh.material;
    m.color.set(c.hex);

    if (c.opacity) {
      m.transparent = true;
      m.opacity = c.opacity;
      m.depthWrite = false;
    } else {
      m.transparent = false;
      m.opacity = 1;
      m.depthWrite = true;
    }

    m.emissive.set(c.glow ? c.glow : 0x000000);
    m.shininess = c.glow ? 8 : 20;
    m.needsUpdate = true;
    render();
  }

  /* --- Swatch dropdown ------------------------------------------------ */

  // The visible colour control. It mirrors the hidden native select,
  // which remains the source of truth, so form submission and the
  // pricing code are unaffected by this being custom.

  function swatchStyle(c) {
    if (c.key === 'transparent') {
      // Show it as translucent rather than inventing a grey.
      return 'background:linear-gradient(135deg,rgba(255,255,255,0.9),rgba(180,200,210,0.55));' +
             'box-shadow:inset 0 0 0 1px rgba(0,0,0,0.18);';
    }
    var style = 'background:' + c.hex + ';';
    if (c.glow) {
      style += 'box-shadow:inset 0 0 0 1px rgba(0,0,0,0.12),0 0 6px 1px ' + c.glow + '55;';
    } else {
      style += 'box-shadow:inset 0 0 0 1px rgba(0,0,0,0.12);';
    }
    return style;
  }

  function closeSwatch() {
    if (!els.swatchList) return;
    els.swatchList.hidden = true;
    if (els.swatchTrigger) els.swatchTrigger.setAttribute('aria-expanded', 'false');
  }

  function openSwatch() {
    if (!els.swatchList) return;
    els.swatchList.hidden = false;
    if (els.swatchTrigger) els.swatchTrigger.setAttribute('aria-expanded', 'true');
    var active = els.swatchList.querySelector('[aria-selected="true"]');
    if (active) active.focus();
  }

  function syncSwatchTrigger() {
    if (!els.swatchChip || !els.color) return;
    var c = currentColor();
    if (!c) return;
    els.swatchChip.setAttribute('style', swatchStyle(c));
    els.swatchName.textContent = c.premium > 0
      ? c.label + ' (+' + cfg.currency_symbol + Number(c.premium).toFixed(2) + ')'
      : c.label;
  }

  function selectSwatch(key) {
    if (!els.color) return;
    els.color.value = key;
    syncSwatchTrigger();
    closeSwatch();
    if (els.swatchTrigger) els.swatchTrigger.focus();
    applyColor();
    update();
  }

  function buildSwatchList() {
    if (!els.swatchList || !els.color) return;
    els.swatchList.innerHTML = '';

    var mat = cfg.materials[els.material.value];
    if (!mat || !mat.colors) return;

    mat.colors.forEach(function (key) {
      var c = cfg.palette[key];
      if (!c) return;
      c.key = key;

      var li = document.createElement('li');
      li.className = 'swatch__option';
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.setAttribute('data-key', key);
      li.setAttribute('aria-selected', key === els.color.value ? 'true' : 'false');

      var chip = document.createElement('span');
      chip.className = 'swatch__chip';
      chip.setAttribute('style', swatchStyle(c));

      var name = document.createElement('span');
      name.className = 'swatch__name';
      name.textContent = c.premium > 0
        ? c.label + ' (+' + cfg.currency_symbol + Number(c.premium).toFixed(2) + ')'
        : c.label;

      // Chip after the name, per the layout: label first, colour to the right.
      li.appendChild(name);
      li.appendChild(chip);

      li.addEventListener('click', function () { selectSwatch(key); });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectSwatch(key);
        }
      });

      els.swatchList.appendChild(li);
    });

    syncSwatchTrigger();
  }

  function bindSwatch() {
    if (!els.swatchTrigger || els.swatchBound) return;
    els.swatchBound = true;

    els.swatchTrigger.addEventListener('click', function () {
      if (els.swatchList.hidden) openSwatch(); else closeSwatch();
    });

    els.swatchTrigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openSwatch();
      }
    });

    els.swatchList.addEventListener('keydown', function (e) {
      var opts = Array.prototype.slice.call(
        els.swatchList.querySelectorAll('.swatch__option'));
      var i = opts.indexOf(document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (opts[i + 1]) opts[i + 1].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (opts[i - 1]) opts[i - 1].focus();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeSwatch();
        els.swatchTrigger.focus();
      }
    });

    document.addEventListener('click', function (e) {
      if (els.swatchRoot && !els.swatchRoot.contains(e.target)) closeSwatch();
    });
  }

  /* --- Rendering results -------------------------------------------- */

  function showError(msg) {
    els.error.textContent = msg;
    els.error.hidden = false;
    els.results.hidden = true;
    if (els.submit) els.submit.disabled = true;
  }

  function clearError() {
    els.error.hidden = true;
    els.error.textContent = '';
  }

  function update() {
    els.infillOut.textContent = els.infill.value + '%';

    var p = price();
    if (!p) return;

    state.price = p.each;

    els.grams.textContent = p.grams.toFixed(0) + ' g';
    els.time.textContent = p.hours < 1
      ? Math.round(p.hours * 60) + ' min'
      : p.hours.toFixed(1) + ' hr';
    els.price.textContent = money(p.total);
    els.unit.textContent = p.qty <= 1 ? ''
      : p.extraCopies > 0
        ? money(p.each) + ' first, ' + money(p.eachExtra) + ' each additional (fits ' + p.perPlate + ' per plate)'
        : money(p.each) + ' each';

    if (els.printer) els.printer.textContent = state.printer.name;

    if (els.surcharge) {
      if (p.surcharge > 0) {
        els.surcharge.textContent = 'Includes a ' + Math.round(p.surcharge * 100) +
          '% large-format charge \u2014 this part is too big for our standard bed.';
        els.surcharge.hidden = false;
      } else {
        els.surcharge.hidden = true;
      }
    }

    if (els.surcharge && p.multicolor) {
      var mcMsg = p.multicolor.count + '-color print' +
        (p.multicolor.sliced ? ' — priced from the slicer’s material estimate.' :
         ' — includes a multicolor allowance for purge waste and swap time.') +
        ' Final price confirmed after we slice the file.';
      // Reuse the surcharge line if the large-format one isn't showing.
      if (els.surcharge.hidden) {
        els.surcharge.textContent = mcMsg;
        els.surcharge.hidden = false;
      }
    }

    if (els.hidden) {
      els.hidden.innerHTML = '';
      var colorText = state.multicolor
        ? mcColorText() + ' (multicolor, ' + state.multicolor.count + ')'
        : (currentColor() ? currentColor().label : '');
      var props = {
        'File': state.fileName,
        'Material': els.material.options[els.material.selectedIndex].text,
        'Color': colorText,
        'Infill': els.infill.value + '%',
        'Quality': els.quality.options[els.quality.selectedIndex].text,
        'Volume': state.volume.toFixed(1) + ' cm3',
        'Dimensions': dimText(),
        'Estimated weight': p.grams.toFixed(0) + ' g',
        'Printer': state.printer.name,
        'Quoted unit price': money(p.each)
      };
      if (p.extraCopies > 0) {
        props['Additional copies'] = p.extraCopies + ' at ' + money(p.eachExtra) + ' each (same plate)';
      }
      if (state.multicolor) {
        props['Multicolor'] = 'Yes — ' + state.multicolor.count + ' colors' +
          (state.multicolor.sliced ? ', slicer estimate' : ', estimated') +
          '. Final price confirmed after slicing.';
      }
      Object.keys(props).forEach(function (k) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + k + ']';
        input.value = props[k];
        els.hidden.appendChild(input);
      });
    }

    // Only unlock checkout once the files are actually stored. If no
    // upload service is configured at all, keep it locked and say so -
    // submitting without one means the cart has nothing to attach the
    // file to and Shopify rejects the add-to-cart outright.
    if (els.submit) {
      if (!cfg.worker_url) {
        els.submit.disabled = true;
        if (els.uploadStatus) {
          els.uploadStatus.textContent = 'Ordering isn’t available yet — check back soon.';
          els.uploadStatus.hidden = false;
        }
      } else {
        els.submit.disabled = !state.uploads;
      }
    }
  }

  function dimText() {
    if (!state.bbox) return '';
    return state.bbox.x.toFixed(1) + ' x ' +
           state.bbox.y.toFixed(1) + ' x ' +
           state.bbox.z.toFixed(1) + ' mm';
  }

  /* --- File handling ------------------------------------------------ */

  function handleFile(file) {
    if (!file) return;
    clearError();

    if (!/\.(stl|3mf)$/i.test(file.name)) {
      showError('That file isn\u2019t an STL or 3MF. Export your model as .stl or .3mf and try again.');
      return;
    }

    if (file.size > cfg.max_file_mb * 1024 * 1024) {
      showError('That file is ' + (file.size / 1048576).toFixed(0) +
        ' MB. The limit is ' + cfg.max_file_mb + ' MB \u2014 email us and we\u2019ll quote it by hand.');
      return;
    }

    els.fileName.textContent = file.name;
    state.fileName = file.name;
    state.file = file;

    var reader = new FileReader();

    reader.onerror = function () {
      showError('Couldn\u2019t read that file. Try re-exporting it.');
    };

    reader.onload = function (e) {
      var buffer = e.target.result;

      var parsing = /\.3mf$/i.test(file.name)
        ? parse3mf(buffer)
        : Promise.resolve().then(function () { return parseStl(buffer); });

      parsing.then(function (parsed) {
        if (!parsed || !parsed.count) {
          showError('No geometry found in that file.');
          return;
        }
        finishMeasure(parsed);
      }).catch(function (err) {
        if (window.console) console.error('model parse failed:', err);
        showError('That file couldn\u2019t be read. It may be corrupt or use features we can\u2019t process \u2014 send it over and we\u2019ll quote it by hand.');
      });
    };

    function finishMeasure(parsed) {
      var m = measure(parsed.tris, parsed.count);

      if (!m.volume || !isFinite(m.volume)) {
        showError('Couldn\u2019t compute a volume. The mesh may not be watertight \u2014 send it over and we\u2019ll check it.');
        return;
      }

      state.volume = m.volume;
      state.bbox = m.bbox;
      state.triangles = parsed.count;
      state.multicolor = parsed.multicolor || null;
      state.regions = parsed.regions || null;
      applyMulticolorUI();

      els.dims.textContent = dimText();
      els.vol.textContent = m.volume.toFixed(1) + ' cm\u00b3';
      els.results.hidden = false;

      // Flag suspicious scale: models exported in cm or inches come
      // through tiny, and the customer should catch that, not us.
      var span = Math.max(m.bbox.x, m.bbox.y, m.bbox.z);
      if (els.scaleNote) {
        els.scaleNote.hidden = span >= 5;
      }

      var printer = choosePrinter(m.bbox);

      if (!printer) {
        var biggest = cfg.printers[cfg.printers.length - 1];
        state.printer = null;
        showError('At ' + dimText() + ' this part is larger than our biggest bed (' +
          biggest.x + ' \u00d7 ' + biggest.y + ' \u00d7 ' + biggest.z +
          ' mm). We can split it into sections and bond them \u2014 get in touch.');
        return;
      }

      state.printer = printer;

      initPreview(parsed.tris, parsed.count, m.bbox);
      update();
      setTimeout(function () {
        capturePreview(uploadAssets);
      }, 400);
    };

    reader.readAsArrayBuffer(file);
  }

  /* --- Wiring ------------------------------------------------------- */

  els.file.addEventListener('change', function () {
    handleFile(els.file.files[0]);
  });

  // Accept a drop anywhere across the upload box and preview area, not
  // just the small dashed box, so it's harder to miss.
  var dropTarget = els.dropZone || els.drop;

  ['dragenter', 'dragover'].forEach(function (evt) {
    dropTarget.addEventListener(evt, function (e) {
      e.preventDefault();
      els.drop.classList.add('is-over');
    });
  });

  ['dragleave', 'drop'].forEach(function (evt) {
    dropTarget.addEventListener(evt, function (e) {
      e.preventDefault();
      els.drop.classList.remove('is-over');
    });
  });

  dropTarget.addEventListener('drop', function (e) {
    var file = e.dataTransfer.files[0];
    if (file && els.file) {
      // Keep the real input in sync so the file still reaches the order.
      try {
        var dt = new DataTransfer();
        dt.items.add(file);
        els.file.files = dt.files;
      } catch (err) { /* older browsers: quote still works, upload may not */ }
    }
    handleFile(file);
  });

  [els.infill, els.quality, els.qty].forEach(function (el) {
    if (el) el.addEventListener('input', update);
  });

  if (els.material) {
    els.material.addEventListener('input', function () {
      populateColors();
      update();
    });
  }

  populateColors();

  els.infillOut.textContent = els.infill.value + '%';

  /* --- Save quote to account --------------------------------------- */

  // A snapshot of the current quote, enough to show it in the account
  // and to pre-fill the tool from a "Resume" link. The STL itself isn't
  // included - file storage waits on the upload service.
  function quotePayload() {
    var p = price();
    var col = currentColor();
    return {
      file_name: state.fileName || '',
      material: els.material.value,
      material_label: els.material.options[els.material.selectedIndex].text,
      color: els.color ? els.color.value : '',
      color_label: col ? col.label : '',
      quality: els.quality.value,
      quality_label: els.quality.options[els.quality.selectedIndex].text,
      infill: parseInt(els.infill.value, 10),
      qty: Math.max(1, parseInt(els.qty.value, 10) || 1),
      volume_cm3: state.volume ? Number(state.volume.toFixed(2)) : null,
      dims_mm: dimText() || null,
      grams: p ? Math.round(p.grams) : null,
      print_hours: p ? Number(p.hours.toFixed(2)) : null,
      printer: state.printer ? state.printer.name : null,
      unit_price: p ? Number(p.each.toFixed(2)) : null,
      total_price: p ? Number(p.total.toFixed(2)) : null,
      currency_symbol: cfg.currency_symbol,
      multicolor: state.multicolor ? {
        count: state.multicolor.count,
        sliced: !!state.multicolor.sliced,
        colors: (state.mcColors || []).slice(),
        file_colors: (state.multicolor.colors || []).slice()
      } : null,
      quoted_at: new Date().toISOString()
    };
  }

  function setSaveStatus(msg, ok) {
    if (!els.saveStatus) return;
    els.saveStatus.textContent = msg || '';
    els.saveStatus.hidden = !msg;
    els.saveStatus.classList.toggle('is-ok', ok === true);
    els.saveStatus.classList.toggle('is-error', ok === false);
  }

  if (els.saveBtn && loggedIn) {
    els.saveBtn.addEventListener('click', function () {
      var payload = quotePayload();
      if (!payload.total_price || !state.file) {
        setSaveStatus('Add your file and pick your options first.', false);
        return;
      }
      els.saveBtn.disabled = true;
      setSaveStatus('Saving…', null);

      var fd = new FormData();
      fd.append('name', payload.file_name || 'Custom quote');
      fd.append('source', 'stl-quote');
      fd.append('payload', JSON.stringify(payload));
      fd.append('stl', state.file, state.fileName || 'model.stl');

      fetch(quotesEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
        body: fd
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (b) {
          return { ok: r.ok, body: b };
        });
      }).then(function (res) {
        els.saveBtn.disabled = false;
        if (res.ok) {
          setSaveStatus('Saved. Find it under "Saved Custom Quotes" in your account.', true);
          return;
        }
        var err = res.body && res.body.error;
        if (err === 'not_logged_in') setSaveStatus('Please log in again to save.', false);
        else if (err === 'quota_exceeded') setSaveStatus('You’ve reached the 25 saved-quote limit — delete a few from your account first.', false);
        else if (err === 'stl_too_large') setSaveStatus('That file is too large to save.', false);
        else if (err === 'stl_bad_type') setSaveStatus('Only .stl and .3mf files can be saved.', false);
        else setSaveStatus('Couldn’t save that. Please try again.', false);
      }).catch(function () {
        els.saveBtn.disabled = false;
        setSaveStatus('Couldn’t save that. Please try again.', false);
      });
    });
  }

  /* --- Pre-fill from a "Resume" link ------------------------------- */

  (function prefillFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var hasResumeParam = false;
    params.forEach(function (v, k) { if (k.indexOf('q_') === 0) hasResumeParam = true; });
    if (!hasResumeParam) return;

    // Pull the saved STL back so the model + price come back, not just the form.
    var qid = params.get('q_id');
    if (qid) {
      setSaveStatus('Loading your saved quote…', null);
      fetch(quotesEndpoint + '/' + encodeURIComponent(qid), {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (r) { return r.json(); }).then(function (b) {
        var url = b && b.quote && b.quote.stl_url;
        var fname = (b && b.quote && b.quote.stl_filename) || 'model.stl';
        var pl = b && b.quote && b.quote.payload;
        if (pl && pl.multicolor && Array.isArray(pl.multicolor.colors)) {
          // Restore the per-region colour picks once the file re-parses.
          state.pendingMcColors = pl.multicolor.colors.slice();
        }
        if (!url) { setSaveStatus('', null); return; }
        return fetch(url).then(function (r) { return r.blob(); }).then(function (blob) {
          var file;
          var mime = /\.3mf$/i.test(fname) ? 'model/3mf' : 'model/stl';
          try {
            file = new File([blob], fname, { type: mime });
          } catch (e) {
            file = blob; file.name = fname;
          }
          setSaveStatus('', null);
          handleFile(file);
        });
      }).catch(function () {
        setSaveStatus('Couldn’t reload the saved file — re-upload it to continue.', false);
      });
    }

    var mat = params.get('q_material');
    if (mat && cfg.materials[mat]) {
      els.material.value = mat;
      populateColors();
    }

    var color = params.get('q_color');
    if (color && els.color) {
      var exists = Array.prototype.some.call(els.color.options, function (o) {
        return o.value === color;
      });
      if (exists) {
        els.color.value = color;
        buildSwatchList();
        syncSwatchTrigger();
        applyColor();
      }
    }

    var qual = params.get('q_quality');
    if (qual && cfg.qualities[qual]) els.quality.value = qual;

    var infill = parseInt(params.get('q_infill'), 10);
    if (infill >= 15 && infill <= 100) els.infill.value = infill;

    var qty = parseInt(params.get('q_qty'), 10);
    if (qty >= 1) els.qty.value = qty;

    els.infillOut.textContent = els.infill.value + '%';
  })();
})();
// sync nudge 1789186870
